import { loadStripe } from '@stripe/stripe-js';

// Singleton Stripe.js loader per Stripe's guidance — `loadStripe` must be called outside
// any component render so the SDK isn't re-fetched on every mount.
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
export const stripePromise = publishableKey ? loadStripe(publishableKey) : Promise.resolve(null);

// SEC-9: card / bank fields live inside an iframe served from js.stripe.com — the raw values
// never cross our origin. These helpers only ever see the resulting pm_xxx (auto-attached by
// Stripe to the SetupIntent's customer).

export async function confirmCardSetup(stripe, clientSecret, cardElement, billingDetails) {
  if (!stripe) {
    return { error: { message: 'Stripe.js is not loaded. Set VITE_STRIPE_PUBLISHABLE_KEY.' } };
  }
  const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
    payment_method: {
      card: cardElement,
      billing_details: billingDetails,
    },
  });
  if (error) return { error };
  return { paymentMethodId: setupIntent.payment_method, status: setupIntent.status };
}

export async function confirmUsBankAccountSetup(stripe, clientSecret, paymentMethodData) {
  if (!stripe) {
    return { error: { message: 'Stripe.js is not loaded. Set VITE_STRIPE_PUBLISHABLE_KEY.' } };
  }
  const { setupIntent, error } = await stripe.confirmUsBankAccountSetup(clientSecret, {
    payment_method: { us_bank_account: paymentMethodData.account, billing_details: paymentMethodData.billing_details },
  });
  if (error) return { error };
  return { paymentMethodId: setupIntent.payment_method, setupIntentId: setupIntent.id, status: setupIntent.status };
}

// Returned by collectBankAccountForSetup when the customer closes Stripe's bank-login window.
const BANK_CONNECTION_CANCELLED = 'bank_connection_cancelled';
const BANK_CONNECTION_CANCELLED_MESSAGE = 'Bank connection cancelled. No bank account was linked.';

/**
 * Financial Connections "instant verification" flow. Hands control to Stripe's hosted bank-login
 * UI — the customer picks their bank, authenticates, and the SetupIntent comes back already
 * verified (no micro-deposit wait). Stripe falls back to micro-deposits automatically when the
 * customer's bank isn't supported, so a successful return doesn't guarantee instant — check
 * `setupIntent.status === 'succeeded'` to confirm. Used in parallel with
 * `confirmUsBankAccountSetup` (manual routing/account flow); the SetupIntent on the backend has
 * `payment_method_options.us_bank_account.financial_connections.permissions = ['payment_method']`
 * which is what lets this work.
 */
export async function collectBankAccountForSetup(stripe, clientSecret, billingDetails) {
  if (!stripe) {
    return { error: { message: 'Stripe.js is not loaded. Set VITE_STRIPE_PUBLISHABLE_KEY.' } };
  }
  // Step 1: customer authenticates with their bank via Financial Connections hosted UI.
  const { setupIntent: collected, error: collectError } = await stripe.collectBankAccountForSetup({
    clientSecret,
    params: {
      payment_method_type: 'us_bank_account',
      payment_method_data: { billing_details: billingDetails },
    },
  });
  if (collectError) return { error: collectError };
  // The customer closed Stripe's bank-login window without linking an account. Stripe resolves
  // WITHOUT an error and leaves the SetupIntent in `requires_payment_method`; confirming now
  // would fail with "A payment method of type us_bank_account was expected to be present…".
  // See https://docs.stripe.com/payments/ach-direct-debit/set-up-payment?payment-ui=elements
  if (collected?.status === 'requires_payment_method') {
    return { error: { code: BANK_CONNECTION_CANCELLED, message: BANK_CONNECTION_CANCELLED_MESSAGE } };
  }
  // Step 2: confirm the SetupIntent — for instant verification this resolves to `succeeded`
  // synchronously; for the fallback async path it lands in `requires_action` / `processing`
  // and the saved method appears as "Verifying with your bank".
  const { setupIntent, error: confirmError } = await stripe.confirmUsBankAccountSetup(clientSecret);
  if (confirmError) return { error: confirmError };
  return {
    paymentMethodId: setupIntent?.payment_method ?? collected?.payment_method,
    setupIntentId: setupIntent?.id ?? collected?.id,
    status: setupIntent?.status ?? collected?.status,
  };
}

/**
 * SEC-39/40/41 — mints a single-use Stripe CVC update token (`cvctok_xxx`) from a
 * `<CardCvcElement>` instance. The CVC value never crosses our origin; the token is the only
 * value transmitted, and Stripe re-verifies it server-side when the PaymentIntent confirms.
 *
 * Returns `{ cvcToken }` on success or `{ error }` if Stripe.js declined. NEVER log the token.
 */
export async function createCvcToken(stripe, cvcElement) {
  if (!stripe) {
    return { error: { code: 'stripe_not_loaded', message: 'Stripe.js is not loaded. Set VITE_STRIPE_PUBLISHABLE_KEY.' } };
  }
  if (!cvcElement) {
    return { error: { code: 'element_not_mounted', message: 'CVV element is not mounted.' } };
  }
  try {
    // 'cvc_update' is Stripe's documented token type for re-verifying CVC against a saved card.
    // See https://docs.stripe.com/api/tokens/create_cvc_update.
    // Not all Stripe accounts / card brands support this — Stripe returns a structured error
    // when unsupported. Callers show it and do not charge without the re-verification (SEC-41).
    // Stripe.js expects the CardCvcElement passed DIRECTLY as the second argument here, not
    // wrapped in any object — wrapping it produces a "You must provide a cardCvc Element"
    // error since Stripe can't find the element instance through the wrapper.
    const { token, error } = await stripe.createToken('cvc_update', cvcElement);
    if (error) return { error };
    return { cvcToken: token?.id };
  } catch (ex) {
    return { error: { code: 'unexpected', message: ex?.message ?? 'CVC token creation threw.' } };
  }
}

// Returns true when MSW is intercepting payment endpoints — Stripe.js calls are short-circuited
// because the fake client_secret won't validate.
export const isMockPaymentsMode = () =>
  String(import.meta.env.VITE_USE_MSW_PAYMENTS).toLowerCase() === 'true';
